/**
 * ------ maintenance history ------
 * 08/13/2019 Yoyo     - refactored as plugined service based widget, business irrelavant. business logic goes into _config
 * 09/07/2019 Yoyo     - call config onDestroy when component is being destroyed
 */
import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';

import { TamDashboardActions, TamDashboardEvents } from '../tam-dashboard.model';
import { filter, debounceTime } from 'rxjs/operators';
import { TamDashboardTileBaseComponent } from '../tam-dashboard-tile.base.component';
import { ChartComponent } from '@progress/kendo-angular-charts';
import { TamDashboardColumnChartTileConfiguration } from './tam-dashboard-column-chart-tile.config';

@Component({
    selector: 'tam-dashboard-column-chart-tile',
    templateUrl: './tam-dashboard-column-chart-tile.component.html',
    styleUrls: ['./tam-dashboard-column-chart-tile.component.scss']
})
export class TamDashboardColumnChartTileComponent implements OnInit, OnDestroy, AfterViewInit, TamDashboardTileBaseComponent {

    @ViewChild('chart', { static: false }) private _chart: ChartComponent;
    @ViewChild('container', { static: false }) private _container: ElementRef;

    data: any;
    seriesColors = ['#63b9bf', '#da8945', '#e6be0a', '#bdd344', '#cc2f64', '#d248bd', '#784f9b'];
    majorGridLines = {
        visible: false
    };
    config: TamDashboardColumnChartTileConfiguration;
    areaHeight = 0;
    areaWidth = 0;

    private _reseizeArea$ = new Subject<string>();
    private _destroySubscriptions: Array<Subscription> = [];
    private _matchPrint: MediaQueryList = window.matchMedia('print');
    private _afterViewFeedback$ = new Subject<string>();

    constructor(
        private _cdr: ChangeDetectorRef,
    ) { }

    ngOnInit() {
        // safari not support addEventListener, support addListener
        if (this._matchPrint) {
            if (this._matchPrint.addEventListener) {
                this._matchPrint.addEventListener('change', this._resizeChart);
            } else if (this._matchPrint['addListener']) {
                this._matchPrint['addListener'](this._resizeChart);
            }
        }
    }

    ngAfterViewInit() {
        this._destroySubscriptions.push(
            this._chart.render.pipe(
                debounceTime(250),
            ).subscribe(res => {
                if (this.config) {
                    res.sender.surface.bind('mouseenter', this.config.onMouseEnter);
                    res.sender.surface.bind('mouseleave', this.config.onMouseLeave);
                }
            })
        );
        this._afterViewFeedback$.next(TamDashboardEvents.TILE_AFTER_VIEW_INIT);
    }

    ngOnDestroy() {
        if (this._matchPrint) {
            if (this._matchPrint.removeEventListener) {
                this._matchPrint.removeEventListener('change', this._resizeChart);
            } else if (this._matchPrint['addListener']) {
                this._matchPrint['removeListener'](this._resizeChart);
            }
            this._resizeChart = null;
        }
        if (this.config && this._chart.surface) {
            this._chart.surface.unbind('mouseenter', this.config.onMouseEnter);
            this._chart.surface.unbind('mouseleave', this.config.onMouseLeave);
        }
        this._destroySubscriptions.forEach(element => element.unsubscribe());
        if (this.config) {
            this.config.destroy();
        }
    }


    categoryLabelContent = (e: any) => {
        if (this.config && this.config.categoryLabelContent) {
            return this.config.categoryLabelContent(e);
        }
        return e.value;
    };

    getStyle() {
        if (this.config && this.config.vertical) {
            return {
                'width': '100%',
                'min-height': this.areaHeight + 'px',
            };
        }
        return {
            'min-width': this.areaWidth + 'px',
            'height': this.areaHeight + 'px',
        };
    }

    labelContent = (e: any) => {
        if (this.config && this.config.labelContent) {
            return this.config.labelContent(e);
        }
        return e.value;
    };

    visual = (args: any): any => {
        const visual = args.createVisual();
        visual.tooltip = args.value;
        return visual;
    };

    @HostListener('window:resize', ['$event'])
    onResize() {
        this._reseizeArea$.next();
    }

    @HostListener('window:beforeprint', ['$event'])
    onBeforePrint() {
        // When printing, min-width affects the chart.resize,
        // So when the chart width does not appear scroll bar, set the min-width value to auto
        if (!this.config.vertical) {
            const containerWidth = this._container.nativeElement.clientWidth;
            if (containerWidth === this.areaWidth) {
                this._chart['element'].nativeElement.style.minWidth = 'auto';
            }
        }
    }

    onSeriesClick(event) {
        if (this.config && this.config.onSeriesClick) {
            this.config.onSeriesClick(event);
        }
    }

    setConfig(config: TamDashboardColumnChartTileConfiguration): boolean {
        // modify reason, same as pie chart
        this.config = config;
        this._init();
        return true;
    }

    private _init() {
        this._destroySubscriptions.forEach(element => element.unsubscribe());
        this._destroySubscriptions.push(
            this.config.actionSubject$.pipe(
                filter((action: any) => action && action.id === this.config.id),
            ).subscribe(action => this._onSubscribeActions(action)),
            this._reseizeArea$.pipe(
                debounceTime(250),
            ).subscribe(() => {
                this._calculateArea();
            }),
            this._afterViewFeedback$.subscribe((payload) => {
                if (payload === TamDashboardEvents.TILE_AFTER_VIEW_INIT) {
                    this.config.feedbackSubject$.next({
                        type: TamDashboardEvents.TILE_AFTER_VIEW_INIT,
                        id: this.config.id
                    });
                }
            })
        );
    }

    private _calculateArea() {
        if (!this._container) {
            return;
        }
        const containerWidth = this._container.nativeElement.clientWidth;
        const containerHeight = this._container.nativeElement.clientHeight;
        const { areaWidth, areaHeight } = this.config.calculateArea(containerWidth, containerHeight);
        this.areaHeight = areaHeight;
        this.areaWidth = areaWidth;
        this._cdr.detectChanges();
    }

    private _resizeChart = (mql) => {
        if (mql.matches) {
            this._chart.resize();
        }
    };

    private _onSubscribeActions(action) {
        if (action.type === TamDashboardActions.SET_TILE_DATA) {
            if (!Array.isArray(action.payload)) {
                return;
            }
            if (action.payload) {
                this.data = action.payload.map(item => ({
                    category: item.category || '(Blank)',
                    value: item.value,
                    valueText: item.valueText
                }));
            } else {
                this.data = action.payload;
            }
            this.config.data = this.data;
            this._cdr.detectChanges();
            this._reseizeArea$.next();
            this.config.feedbackSubject$.next({
                type: TamDashboardEvents.TILE_LOADED_DATA,
                id: this.config.id
            });
        }
    }
}
